import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { markdownRenderers } from "../markdown/markdown-renderers";
import "./markdown-editor.component.scss";

interface IMarkdownEditorProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  rows?: number;
}

type Mode = "write" | "preview";

/** Distinguishes concurrent uploads so each placeholder is replaced exactly once. */
let uploadSequence = 0;

async function uploadImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch("/api/upload", { method: "POST", body: formData });
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status} ${res.statusText})`);
  }
  const data = await res.json();
  if (!data?.url) {
    throw new Error("Upload succeeded but returned no URL");
  }
  return data.url;
}

/**
 * Markdown editor shared by the create and edit views. Preview goes through the
 * same renderers as the published article, so code highlighting and PlantUML
 * blocks look the same here as they will on the post.
 */
export default function MarkdownEditor({
  value,
  onChange,
  placeholder = "Write your post in Markdown. Paste or drop an image to upload it.",
  rows = 18,
}: IMarkdownEditorProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingSelection = useRef<[number, number] | null>(null);
  const [mode, setMode] = useState<Mode>("write");
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState<string | null>(null);

  /**
   * The latest value, readable from async callbacks. An upload that resolves
   * after further typing must not write back a stale snapshot of the document.
   */
  const valueRef = useRef(value);
  valueRef.current = value;

  /** Every edit routes through here so React state stays the source of truth. */
  const applyEdit = useCallback(
    (next: string, selectionStart: number, selectionEnd: number) => {
      pendingSelection.current = [selectionStart, selectionEnd];
      onChange(next);
    },
    [onChange]
  );

  useLayoutEffect(() => {
    const selection = pendingSelection.current;
    if (!selection || !textareaRef.current) return;
    pendingSelection.current = null;
    textareaRef.current.focus();
    textareaRef.current.setSelectionRange(selection[0], selection[1]);
  });

  const wrapSelection = (before: string, after: string, placeholderText: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.slice(start, end) || placeholderText;
    const next =
      value.slice(0, start) + before + selected + after + value.slice(end);
    applyEdit(next, start + before.length, start + before.length + selected.length);
  };

  const prefixLines = (prefix: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const lineStart = value.lastIndexOf("\n", textarea.selectionStart - 1) + 1;
    const rawEnd = value.indexOf("\n", textarea.selectionEnd);
    const lineEnd = rawEnd === -1 ? value.length : rawEnd;
    const block = value
      .slice(lineStart, lineEnd)
      .split("\n")
      .map((line) => prefix + line)
      .join("\n");
    const next = value.slice(0, lineStart) + block + value.slice(lineEnd);
    applyEdit(next, lineStart, lineStart + block.length);
  };

  const insertBlock = (snippet: string) => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? value.length;
    const end = textarea?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + snippet + value.slice(end);
    applyEdit(next, start + snippet.length, start + snippet.length);
  };

  /**
   * Drops a placeholder in immediately, then swaps it for the real Markdown once
   * the upload lands. Both steps go through onChange, which is what the previous
   * setRangeText() approach skipped — the link was written straight to the DOM
   * and never reached React state, so it vanished on submit.
   */
  const uploadAndInsert = useCallback(
    async (files: File[]) => {
      const images = files.filter((file) => file.type.startsWith("image/"));
      if (images.length === 0) return;

      setError(null);
      setUploading((count) => count + images.length);

      await Promise.all(
        images.map(async (file) => {
          const token = `![uploading ${(uploadSequence += 1)}…]()`;
          const withToken = valueRef.current + (valueRef.current.endsWith("\n") || valueRef.current === "" ? "" : "\n") + token + "\n";
          valueRef.current = withToken;
          onChange(withToken);

          try {
            const url = await uploadImage(file);
            const alt = file.name.replace(/\.[^.]+$/, "");
            const settled = valueRef.current.replace(token, `![${alt}](${url})`);
            valueRef.current = settled;
            onChange(settled);
          } catch (err) {
            const cleaned = valueRef.current.replace(`${token}\n`, "").replace(token, "");
            valueRef.current = cleaned;
            onChange(cleaned);
            setError(err instanceof Error ? err.message : "Image upload failed");
          } finally {
            setUploading((count) => count - 1);
          }
        })
      );
    },
    [onChange]
  );

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(event.clipboardData.files);
    if (files.some((file) => file.type.startsWith("image/"))) {
      // Otherwise the browser also pastes its own representation of the image.
      event.preventDefault();
      void uploadAndInsert(files);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLTextAreaElement>) => {
    const files = Array.from(event.dataTransfer.files);
    if (files.some((file) => file.type.startsWith("image/"))) {
      event.preventDefault();
      void uploadAndInsert(files);
    }
  };

  const handleFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    void uploadAndInsert(Array.from(event.target.files ?? []));
    event.target.value = "";
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!(event.metaKey || event.ctrlKey)) return;
    const key = event.key.toLowerCase();
    if (key === "b") {
      event.preventDefault();
      wrapSelection("**", "**", "bold text");
    } else if (key === "i") {
      event.preventDefault();
      wrapSelection("_", "_", "italic text");
    } else if (key === "k") {
      event.preventDefault();
      wrapSelection("[", "](https://)", "link text");
    }
  };

  return (
    <div className="md-editor">
      <div className="md-editor-bar">
        <div className="md-editor-tools" role="group" aria-label="Formatting">
          <button type="button" onClick={() => prefixLines("## ")} title="Heading">
            H
          </button>
          <button
            type="button"
            className="md-bold"
            onClick={() => wrapSelection("**", "**", "bold text")}
            title="Bold (⌘B)"
          >
            B
          </button>
          <button
            type="button"
            className="md-italic"
            onClick={() => wrapSelection("_", "_", "italic text")}
            title="Italic (⌘I)"
          >
            I
          </button>
          <button
            type="button"
            onClick={() => wrapSelection("[", "](https://)", "link text")}
            title="Link (⌘K)"
          >
            Link
          </button>
          <button
            type="button"
            onClick={() => wrapSelection("`", "`", "code")}
            title="Inline code"
          >
            {"</>"}
          </button>
          <button
            type="button"
            onClick={() => insertBlock("\n```\n\n```\n")}
            title="Code block"
          >
            Block
          </button>
          <button type="button" onClick={() => prefixLines("- ")} title="Bullet list">
            List
          </button>
          <button type="button" onClick={() => prefixLines("> ")} title="Quote">
            Quote
          </button>
          <label className="md-editor-upload" title="Upload image">
            Image
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFilePicked}
            />
          </label>
        </div>
        <div className="md-editor-modes" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "write"}
            className={mode === "write" ? "is-active" : ""}
            onClick={() => setMode("write")}
          >
            Write
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "preview"}
            className={mode === "preview" ? "is-active" : ""}
            onClick={() => setMode("preview")}
          >
            Preview
          </button>
        </div>
      </div>

      {mode === "write" ? (
        <textarea
          ref={textareaRef}
          className="md-editor-textarea"
          value={value}
          rows={rows}
          placeholder={placeholder}
          spellCheck
          onChange={(event) => onChange(event.target.value)}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={(event) => event.preventDefault()}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <div className="md-editor-preview article-display-div">
          {value.trim() ? (
            <ReactMarkdown components={markdownRenderers}>{value}</ReactMarkdown>
          ) : (
            <p className="md-editor-empty">Nothing to preview yet.</p>
          )}
        </div>
      )}

      <div className="md-editor-status">
        {uploading > 0 ? (
          <span className="md-editor-uploading">
            Uploading {uploading} image{uploading > 1 ? "s" : ""}…
          </span>
        ) : (
          <span className="md-editor-hint">
            Markdown supported. Paste or drop an image to upload it.
          </span>
        )}
        {error ? <span className="md-editor-error">{error}</span> : null}
      </div>
    </div>
  );
}
